import React, { useState } from 'react';
import { Button, Modal, notification, Skeleton, Space } from 'antd';
import PropTypes from 'prop-types';
import {
  PayPalButtons,
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js';
import { Environment } from '../../utils/Environment';
import useCaseTransactionStatus from '../../hooks/cases/useCaseTransactionStatus';
import usePaymentTransactionOrder from '../../hooks/cases/usePaymentTransactionOrder';
import { Typography } from 'antd';
import useInvoiceTransactionStatus from '../../hooks/cases/useInvoiceTransactionStatus';
import { InvoiceType, PaymentFor } from '../../appContants';
import { useNavigate } from 'react-router-dom';
const { Text } = Typography;

const PAYPAL_CLIENT_ID = Environment?.paypalClientId();

const PaypalButton = ({
  handleCreateOrder,
  handleCompleteOrder,
  handleCancel,
  handleError,
}) => {
  const [{ isPending }] = usePayPalScriptReducer();
  return isPending ? (
    <Skeleton.Button block className="mt-4" active size="large" />
  ) : (
    <PayPalButtons
      className="mt-5"
      style={{ layout: 'vertical' }}
      createOrder={handleCreateOrder}
      onApprove={handleCompleteOrder}
      onCancel={handleCancel}
      onError={handleError}
    />
  );
};
PaypalButton.propTypes = {
  handleCreateOrder: PropTypes.func,
  handleCompleteOrder: PropTypes.func,
  handleCancel: PropTypes.func,
  handleError: PropTypes.func,
};

const PaypalPaymentButton = ({
  paymentFor,
  recordId,
  loading,
  disabled,
  onMarkAsSubmitted,
  title,
  type,
  htmlType,
  icon = null,   // default icon here,
}) => {
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();
  const [open, setOpen] = useState(false);
  const { loading: caseTransactionStatus, getData: getCaseData } =
    useCaseTransactionStatus();
  const { loading: invoiceTransactionStatus, getData: getInvoiceData } =
    useInvoiceTransactionStatus();
  const {
    loading: loadingTransactionOrder,
    createOrder,
    completeOrder,
    cancelOrder,
  } = usePaymentTransactionOrder();

  const handleButtonClick = async () => {
    let response;
    if (paymentFor === PaymentFor.CASE) {
      response = await getCaseData(recordId);
    } else {
      response = await getInvoiceData(recordId);
    }

    if (paymentFor === PaymentFor.CASE) {
      const notificationObj = {
        message: 'Payment Pending',
        description: (
          <Space direction="vertical">
            <Text>
              You have invoices with pending payments. Please check your
              invoices and make the payments accordingly. Only then can you
              submit any new cases.
            </Text>
            <Button
              // type="primary"
              size="small"
              onClick={() => navigate('/user/invoicelist')}
            >
              View Invoices
            </Button>
          </Space>
        ),
      };
      const isMonthly = response?.paymentMethod === InvoiceType.MONTHLY;
      const isPayAsYouGo =
        response?.paymentMethod === InvoiceType.PAY_AS_YOU_GO;
      // Check if any payment is pending
      // Check if clinic location payment method is `MONTHLY`
      if (isMonthly) {
        if (response?.hasPendingMonthlyInvoices) {
          return api.error(notificationObj);
        } else {
          // can submit case if there are no pending invoice payments, as it falls under the MONTHLY payment method
          return onMarkAsSubmitted?.();
        }
      } else if (isPayAsYouGo) {
        if (response?.hasPendingMonthlyInvoices) {
          return api.error(notificationObj);
        } else {
          // Do nothing... Allow to make payment for the given case
        }
      }
    }
    // Check if payment is already done
    if (response?.hasCompletedTransactions) {
      return api.error({ message: 'Payment already done!' });
      // if there is any active payment
    } else if (response?.hasActiveTransactions) {
      return api.error({
        message: 'There are active payments in progress for this record!',
      });
    } else {
      setOpen(true);
    }
  };
  const closeModal = () => setOpen(false);

  const handleCreateOrder = async () => {
    try {
      const response = await createOrder(recordId, paymentFor);
      return response?.orderNo;
    } catch (error) {
      api.error({ message: 'Payment failed!', description: error?.message });
      closeModal();
    }
  };

  const handleCompleteOrder = async (data) => {
    const orderId = data?.orderID;
    try {
      await completeOrder(orderId);
      api.success({ message: 'Payment completed successfully!' });
      onMarkAsSubmitted?.();
      closeModal();
    } catch (error) {
      api.error({ message: 'Payment failed!' });
      handleCancelOrder(orderId);
    }
  };

  const handleCancel = async (data) => {
    const orderId = data?.orderID;
    api.error({ message: 'Payment cancelled!' });
    handleCancelOrder(orderId);
  };

  const handleError = () => {
    api.error({ message: 'Payment failed!' });
    closeModal();
  };
  /**
   * handleCancelOrder
   * @description Cancel order
   */
  const handleCancelOrder = (orderId) => {
    try {
      cancelOrder(orderId);
      closeModal();
    } catch (error) {
      console.log('Error on payment cancel: ', error);
    }
  };

  return (
    <>
      {contextHolder}
      <Button
        icon={icon}
        loading={
          caseTransactionStatus ||
          invoiceTransactionStatus ||
          loading ||
          loadingTransactionOrder
        }
        type={type}
        htmlType={htmlType}
        onClick={handleButtonClick}
        disabled={disabled}
      >
        {icon ? null : title}
      </Button>
      <Modal
        title="Make Payment"
        open={open}
        onCancel={() => setOpen(false)}
        footer={false}
      >
        <div>
          <Space direction="vertical" size="middle">
            <Text type="secondary">
              {paymentFor === PaymentFor.CASE
                ? 'Please make payment using the button below, in order to submit the case!'
                : 'Please make payment using the button below!'}
            </Text>
          </Space>
          <PayPalScriptProvider
            options={{
              clientId: PAYPAL_CLIENT_ID,
              disableFunding: 'credit,card,paylater',
            }}
          >
            <PaypalButton
              handleCancel={handleCancel}
              handleCompleteOrder={handleCompleteOrder}
              handleCreateOrder={handleCreateOrder}
              handleError={handleError}
            />
          </PayPalScriptProvider>
        </div>
      </Modal>
    </>
  );
};

PaypalPaymentButton.propTypes = {
  recordId: PropTypes.number.isRequired,
  /**
   * paymentFor: PaymentFor
   */
  paymentFor: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onMarkAsSubmitted: PropTypes.func,
  title: PropTypes.string,
  type: PropTypes.string,
  htmlType: PropTypes.string,
  icon: PropTypes.node,
};

export default PaypalPaymentButton;
